import { useState, useRef } from 'react';
import { Modal, Box, Button } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faCheckCircle, faCircleXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import '../translations.js';
import './uploadFileModal.css';

import { MAX_SOURCE_FILE_BYTES } from '../utils/knowledgeLimits.js';
// Use the raw fetch to avoid the global interceptor adding an Authorization header
// which would conflict with Azure SAS token authentication
const rawFetch = window._originalFetch || window.fetch;

// The document extensions the backend can actually ingest. The upload path runs
// through extract_text_from_file, whose ext_to_mime map is a HARD allow-list: an
// extension that is not in it returns None and the ingest fails with no usable
// reason for the customer. Keep this in sync with that map
// (backend src/controller/knowledge.py) — it is the contract, not a preference.
//
// No .html on purpose: an uploaded HTML file would be embedded raw, markup and
// all. Live pages go through Website Links, which cleans the page first.
const DEFAULT_ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'rtf', 'txt', 'csv', 'md'];
const DEFAULT_ACCEPT_ATTR = '.pdf,.doc,.docx,.rtf,.txt,.csv,.md';
const DEFAULT_FILE_TAGS = ['.pdf', '.doc', '.docx', '.rtf', '.txt', '.csv', '.md'];

const UploadFileModal = ({
  open,
  onClose,
  onSubmit,
  remainingSlots = Infinity,
  // Validation is extension-based for every caller. Browsers report MIME types
  // inconsistently across OS and file type (a .csv arrives as text/csv,
  // application/vnd.ms-excel or ''; a .rtf as application/rtf, text/rtf or
  // application/msword; a .md usually as ''), so a MIME allow-list rejects files
  // the backend handles fine. The extension is also exactly what the backend
  // gates on, so this check mirrors the server instead of guessing alongside it.
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  acceptAttr = DEFAULT_ACCEPT_ATTR,
  fileTags = DEFAULT_FILE_TAGS,
}) => {
  const MAX_FILES_DEFAULT = 10;
  // Per-file cap mirrors the backend's ingest_limits (10 MB, all sources).
  const MAX_FILE_SIZE_BYTES = MAX_SOURCE_FILE_BYTES;
  const MAX_TOTAL_SIZE_MB = 100;
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;
  const MAX_FILES = Math.min(MAX_FILES_DEFAULT, remainingSlots);

  // A name with no dot at all yields the whole name here, which never matches the
  // list — extension-less files are rejected, which is correct: the backend
  // sanitizer turns them into a .bin blob that then fails extraction.
  const isAllowedFile = (file) => {
    const parts = (file.name || '').split('.');
    if (parts.length < 2) return false;
    return allowedExtensions.includes((parts.pop() || '').toLowerCase());
  };

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [invalidTypeFiles, setInvalidTypeFiles] = useState([]);
  const [tooLargeFiles, setTooLargeFiles] = useState([]);
  const [exceedsTotalLimitFiles, setExceedsTotalLimitFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dropRef = useRef(null);

  const uploadFileToAzure = async (file) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}get-azure-sas`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_name: file.name })
        }
      );
      if (!res.ok) throw new Error('Failed to get SAS URL from backend');
      const { sasUrl } = await res.json();
      if (!sasUrl) throw new Error('SAS URL not returned');

      await rawFetch(sasUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      });

      return sasUrl.split('?')[0];
    } catch (err) {
      console.error('Error uploading to Azure:', err);
      throw err;
    }
  };

  const handleFiles = async (files) => {
    const incomingFiles = Array.from(files);
    const slotsAvailable = MAX_FILES - selectedFiles.length;
    let maxFilesExceeded = false;

    const acceptedFiles = incomingFiles.slice(0, Math.max(0, slotsAvailable));
    if (incomingFiles.length > slotsAvailable) {
      maxFilesExceeded = true;
    }

    if (acceptedFiles.length === 0) {
      setFileError(t('max_files_exceeded'));
      return;
    }

    const filesToProcess = acceptedFiles.map(f => ({ name: f.name, size: f.size }));
    setUploadingFiles(filesToProcess);
    setIsUploading(true);
    const valid = [];
    const invalidType = [];
    const tooLarge = [];
    const exceedTotalLimit = [];

    let currentTotalValidSize = selectedFiles.reduce((acc, f) => acc + f.size, 0);

    for (const file of acceptedFiles) {
      if (!isAllowedFile(file)) {
        invalidType.push(file);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        tooLarge.push(file);
      } else {
        if (currentTotalValidSize + file.size <= MAX_TOTAL_SIZE_BYTES) {
          try {
            const azureUrl = await uploadFileToAzure(file);
            valid.push({
              name: file.name,
              size: file.size,
              type: file.type,
              azureUrl
            });
            currentTotalValidSize += file.size;
          } catch {
          }
        } else {
          exceedTotalLimit.push(file);
        }
      }
    }

    setSelectedFiles(prev => [...prev, ...valid]);
    setInvalidTypeFiles(prev => [...prev, ...invalidType]);
    setTooLargeFiles(prev => [...prev, ...tooLarge]);
    setExceedsTotalLimitFiles(prev => [...prev, ...exceedTotalLimit]);

    if (invalidType.length || tooLarge.length || exceedTotalLimit.length || maxFilesExceeded) {
      const msgs = [];
      if (maxFilesExceeded) {
        const skipped = incomingFiles.length - acceptedFiles.length;
        msgs.push(t('max_files_exceeded_partial', { count: skipped, max: MAX_FILES }));
      }
      if (invalidType.length) msgs.push(t('upload_n_invalid_type', { count: invalidType.length }));
      if (tooLarge.length) msgs.push(t('upload_n_too_large', { count: tooLarge.length }));
      if (exceedTotalLimit.length) msgs.push(t('upload_n_exceed_total', { count: exceedTotalLimit.length }));
      setFileError(
        msgs.join(', ') +
          (invalidType.length || tooLarge.length || exceedTotalLimit.length
            ? ` ${t('file_will_be_ignored')}`
            : '')
      );
    } else {
      setFileError('');
    }
    setIsUploading(false);
    setUploadingFiles([]);
  };

  const handleFileChange = (e) => handleFiles(Array.from(e.target.files || []));
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles(prevSelected => {
      const newSelected = [...prevSelected];
      newSelected.splice(indexToRemove, 1);

      let currentTotalValidSize = newSelected.reduce((acc, f) => acc + f.size, 0);

      setExceedsTotalLimitFiles(prevExceed => {
        const updatedExceed = [...prevExceed];
        let i = 0;
        while (i < updatedExceed.length) {
          const file = updatedExceed[i];
          if (file.size + currentTotalValidSize <= MAX_TOTAL_SIZE_BYTES) {
            updatedExceed.splice(i, 1);
            currentTotalValidSize += file.size;
          } else {
            i++;
          }
        }
        return updatedExceed;
      });

      return newSelected;
    });
  };

  // ─────────────────────────────────────────────────────
  // Close / reset
  // ─────────────────────────────────────────────────────
  const resetState = () => {
    setSelectedFiles([]);
    setInvalidTypeFiles([]);
    setTooLargeFiles([]);
    setExceedsTotalLimitFiles([]);
    setFileError('');
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleSubmit = () => {
    if (selectedFiles.length === 0) return; // nothing to send

    // First arg: the Azure URLs (unchanged contract — existing callers rely on it).
    // Second arg (optional): per-file metadata so a caller can show the real file
    // names. Existing callers simply ignore the extra argument.
    onSubmit(
      selectedFiles.map(f => f.azureUrl),
      selectedFiles.map(f => ({ name: f.name, url: f.azureUrl, size: f.size })),
    );
    resetState();
    onClose();
  };

  // ─────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') handleCancel();
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: 4,
          padding: isMobile ? '15px' : '20px',
          width: isMobile ? '85%' : '90%',
          maxWidth: 500
        }}
      >
        {/* Upload area – same markup & classes */}
        <div
          ref={dropRef}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => e.preventDefault()}
          className="file-upload-container"
          style={{
            border: '2px dashed #ccc',
            padding: '20px',
            borderRadius: '10px',
            textAlign: 'center',
            background: '#fafafa'
          }}
        >
          <div className="file-drag-drop">
            <FontAwesomeIcon icon={faArrowUpFromBracket} />
            {t('drag_and_drop_here')}
          </div>

          <div className="add-bot-file-tag-parent">
            {fileTags.map((tag) => (
              <div className="add-bot-file-tag" key={tag}>{tag}</div>
            ))}
          </div>

          <button
            className="upload-files-button"
            onClick={() => document.getElementById('multiFileInput').click()}
          >
            {t('select_files')}
          </button>

          <input
            id="multiFileInput"
            type="file"
            multiple
            accept={acceptAttr}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {fileError && (
            <div className="file-error-banner">
              <FontAwesomeIcon
                icon={faCircleXmark}
                style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }}
              />
              <span>{fileError}</span>
            </div>
          )}

          {isUploading && uploadingFiles.length > 0 && (
            <div className="file-parent-container">
              <div className="files-container">
                {uploadingFiles.map((file, idx) => (
                  <div key={file.name + idx} className="valid-file">
                    <div className="file-content">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        style={{ color: '#5616ea', fontSize: 14, marginRight: 8 }}
                      />
                      <span>
                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isUploading && (selectedFiles.length > 0 ||
            invalidTypeFiles.length > 0 ||
            tooLargeFiles.length > 0 ||
            exceedsTotalLimitFiles.length > 0) && (
            <div className="file-parent-container">
              {/* ✅ VALID */}
              {selectedFiles.length > 0 && (
                <div className="files-container">
                  {selectedFiles.map((file, idx) => (
                    <div key={file.name + idx} className="valid-file">
                      <div className="file-content">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{ color: '#5616ea', fontSize: 14, marginRight: 8 }}
                        />
                        <span>
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFile(idx)}
                        style={{ minWidth: '30px', fontWeight: 600 }}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* ❌ INVALID TYPE */}
              {invalidTypeFiles.length > 0 && (
                <>
                  <div className="invalid-header-text">
                    {t('invalid_file_type')}
                  </div>
                  <div className="files-container">
                    {invalidTypeFiles.map((file, idx) => (
                      <div key={file.name + idx} className="invalid-file">
                        <div className="file-content">
                          <FontAwesomeIcon
                            icon={faCircleXmark}
                            style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }}
                          />
                          <span>{file.name}</span>
                        </div>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            setInvalidTypeFiles(prev => prev.filter((_, i) => i !== idx))
                          }
                          style={{ minWidth: '30px', fontWeight: 600 }}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ❌ TOO LARGE */}
              {tooLargeFiles.length > 0 && (
                <>
                  <div className="invalid-header-text">
                    {t('file_too_large') || 'File(s) too large'}
                  </div>
                  <div className="files-container">
                    {tooLargeFiles.map((file, idx) => (
                      <div key={file.name + idx} className="invalid-file">
                        <div className="file-content">
                          <FontAwesomeIcon
                            icon={faCircleXmark}
                            style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }}
                          />
                          <span>
                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            setTooLargeFiles(prev => prev.filter((_, i) => i !== idx))
                          }
                          style={{ minWidth: '30px', fontWeight: 600 }}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ❌ EXCEEDS TOTAL */}
              {exceedsTotalLimitFiles.length > 0 && (
                <>
                  <div className="invalid-header-text">
                    {t('exceeds_total_limit') ||
                      'File(s) exceed total 100 MB limit'}
                  </div>
                  <div className="files-container">
                    {exceedsTotalLimitFiles.map((file, idx) => (
                      <div key={file.name + idx} className="invalid-file">
                        <div className="file-content">
                          <FontAwesomeIcon
                            icon={faCircleXmark}
                            style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }}
                          />
                          <span>
                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            setExceedsTotalLimitFiles(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                          style={{ minWidth: '30px', fontWeight: 600 }}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: 20 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{
              color: '#5616ea', // text color
              borderColor: '#5616ea', // border color
              '&:hover': {
                borderColor: '#5e0fb3',
                backgroundColor: '#f3eaff', // optional hover background
              },
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: '#5616ea',
              '&:hover': {
                backgroundColor: '#4a14d6',
              },
            }}>
              {t('upload')}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default UploadFileModal;
